import {Box, Flex} from 'exsportia-components'
import {FC, useCallback, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {UserExsportiaCard} from '../../components/user-exsportia-card/user-exsportia-card'
import {AuthService} from '../../services/auth'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {PhoneLoginDobStep} from './phone-login-dob-step'
import {PhoneLoginEmailStep} from './phone-login-email-step'
import {PhoneLoginNameStep} from './phone-login-name-step'
import {PhoneLoginOtpStep} from './phone-login-otp-step'
import {PhoneLoginPhoneStep} from './phone-login-phone-step'
import {PhoneLoginPhotoStep} from './phone-login-photo-step'

type Props = {
  backToEmail: (email?: string) => void
  venueId?: string
}

export const PhoneLoginStep: FC<Props> = ({backToEmail, venueId}) => {
  const dispatch = useDispatch()
  const {l} = useLocale()
  const reduxStep = useSelector(AuthService.selectors.phoneLoginStep())
  const isLoading = useSelector(AuthService.selectors.phoneLoginLoading())
  const error = useSelector(AuthService.selectors.phoneLoginError())
  const {closeAllModals} = useModalActions()

  const [localStep, setLocalStep] = useState<string | null>(null)
  const step = localStep || reduxStep

  const [phoneNumber, setPhoneNumber] = useState('')
  const [collector, setCollector] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    avatar: null as string | null,
  })

  const handleClose = useCallback(() => {
    closeAllModals()
    dispatch(AuthService.actions.phoneLoginResetAction())
    setLocalStep(null)
  }, [closeAllModals, dispatch])

  useEffect(() => {
    if (step === 'success') {
      const timer = setTimeout(handleClose, 3000)
      return () => clearTimeout(timer)
    }
  }, [step, handleClose])

  useEffect(() => {
    if (!reduxStep) {
      dispatch(AuthService.actions.phoneLoginStep('phone'))
    }
  }, [reduxStep, dispatch])

  useEffect(() => {
    return () => {
      dispatch(AuthService.actions.phoneLoginResetAction())
    }
  }, [dispatch])

  return (
    <div>

      {step === 'name' && (
        <PhoneLoginNameStep
          initialData={{firstName: collector.firstName, lastName: collector.lastName}}
          onNext={(data) => {
            setCollector((prev) => ({...prev, ...data}))
            setLocalStep('dob')
          }}
          onBack={() => {
            dispatch(AuthService.actions.phoneLoginStep('phone'))
            setLocalStep(null)
          }}
        />
      )}

      {step === 'dob' && (
        <PhoneLoginDobStep
          initialDob={collector.dob}
          onNext={(dob) => {
            setCollector((prev) => ({...prev, dob}))
            setLocalStep('email')
          }}
          onSkip={() => setLocalStep('email')}
          onBack={() => setLocalStep('name')}
        />
      )}

      {step === 'email' && (
        <PhoneLoginEmailStep
          initialEmail={collector.email}
          onNext={(email) => {
            setCollector((prev) => ({...prev, email}))
            setLocalStep('photo')
          }}
          onBack={() => setLocalStep('dob')}
          error={error}
          isLoading={isLoading}
        />
      )}

      {step === 'photo' && (
        <PhoneLoginPhotoStep
          initialAvatar={collector.avatar}
          isLoading={isLoading}
          onBack={() => setLocalStep('email')}
          onFinish={(avatar) => {
            setCollector((prev) => ({...prev, avatar}))
            dispatch(
              AuthService.actions.phoneLoginLinkEmailAction({
                ...collector,
                avatar,
                venueId,
                callback: () => {
                  setLocalStep('success')
                },
                onConflict: (email) => {
                  dispatch(AuthService.actions.phoneLoginResetAction())
                  backToEmail(email)
                  setLocalStep(null)
                },
              }),
            )
          }}
          onSkip={() => {
            dispatch(
              AuthService.actions.phoneLoginLinkEmailAction({
                ...collector,
                venueId,
                callback: () => {
                  setLocalStep('success')
                },
                onConflict: (email) => {
                  dispatch(AuthService.actions.phoneLoginResetAction())
                  backToEmail(email)
                  setLocalStep(null)
                },
              }),
            )
          }}
        />
      )}

      {step === 'success' && (
        <Box pb='24px'>
          <Flex
            flexDirection='column'
            alignItems='center'
            gap='16px'
          >
            <UserExsportiaCard
              name={`${collector.firstName} ${collector.lastName}`}
              avatar={collector.avatar}
              subText={collector.email}
            />
            <Box
              mt='8px'
              width='100%'
            >
              <Button onClick={handleClose}>{l('finish', {defaultMessage: 'Finish'})}</Button>
            </Box>
          </Flex>
        </Box>
      )}

      {step === 'otp' && (
        <PhoneLoginOtpStep
          phoneNumber={phoneNumber}
          backAction={() => {
            dispatch(AuthService.actions.phoneLoginStep('phone'))
            dispatch(AuthService.actions.phoneLoginError(null))
            setLocalStep(null)
          }}
        />
      )}

      {(step === 'phone' || !step) && (
        <PhoneLoginPhoneStep
          initialPhone={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          backAction={() => {
            dispatch(AuthService.actions.phoneLoginResetAction())
            backToEmail()
            setLocalStep(null)
          }}
        />
      )}
    </div>
  )
}
